import { z } from 'zod';
import type { TFunction } from 'i18next';

/** `TimeOff.Domain.Enums.ApprovalLevel` — confirmed exact backend values. */
export const APPROVAL_LEVEL_OPTIONS = ['SingleApprover', 'FullChain'] as const;
/** `TimeOff.Domain.Enums.ResponseLimitUnit`. */
export const RESPONSE_LIMIT_UNIT_OPTIONS = ['BusinessDays', 'CalendarDays'] as const;
/** `TimeOff.Domain.Enums.OnExhaustedAction`. */
export const ON_EXHAUSTED_OPTIONS = ['AutoApprove', 'AutoReject'] as const;
/** `TimeOff.Domain.Enums.CarryOverMode`. */
export const CARRY_OVER_MODE_OPTIONS = ['Accumulate', 'Forfeit'] as const;

/**
 * Tenant policy form schema.
 *
 * @remarks
 * `approvalLevel`/`responseLimitUnit`/`onExhausted`/`carryOverMode` are
 * `z.enum` against the confirmed backend value sets above (each a 2-value
 * `int` enum server-side, validated there via `Enum.IsDefined` — an
 * out-of-set value gets a `400`, not silently accepted). The `Select` in the
 * UI already constrains input to these same values; this schema is the
 * second guard.
 */
export const policySchema = (t: TFunction) =>
  z
    .object({
      approvalLevel: z.enum(APPROVAL_LEVEL_OPTIONS, {
        error: t('timeOff:errors.approvalLevelRequired'),
      }),
      responseLimit: z.coerce
        .number({ error: t('timeOff:errors.responseLimitRequired') })
        .int({ error: t('timeOff:errors.responseLimitInt') })
        .min(1, { error: t('timeOff:errors.responseLimitRange') })
        .max(365, { error: t('timeOff:errors.responseLimitRange') }),
      responseLimitUnit: z.enum(RESPONSE_LIMIT_UNIT_OPTIONS, {
        error: t('timeOff:errors.responseLimitUnitRequired'),
      }),
      onExhausted: z.enum(ON_EXHAUSTED_OPTIONS, {
        error: t('timeOff:errors.onExhaustedRequired'),
      }),
      minAdvanceBusinessDays: z.coerce
        .number({ error: t('timeOff:errors.minAdvanceRequired') })
        .int()
        .min(0, { error: t('timeOff:errors.minAdvanceRange') }),
      resolutionMarginBusinessDays: z.coerce
        .number({ error: t('timeOff:errors.resolutionMarginRequired') })
        .int()
        .min(0, { error: t('timeOff:errors.resolutionMarginRange') }),
      graceToleranceDays: z.coerce
        .number({ error: t('timeOff:errors.graceToleranceRequired') })
        .int()
        .min(0, { error: t('timeOff:errors.graceToleranceRange') }),
      carryOverMode: z.enum(CARRY_OVER_MODE_OPTIONS, {
        error: t('timeOff:errors.carryOverModeRequired'),
      }),
      allowAdvance: z.boolean(),
      maxAdvanceDays: z.coerce
        .number({ error: t('timeOff:errors.maxAdvanceRequired') })
        .int()
        .min(0),
    })
    .refine((policy) => policy.allowAdvance || policy.maxAdvanceDays === 0, {
      error: t('timeOff:errors.maxAdvanceMustBeZero'),
      path: ['maxAdvanceDays'],
    })
    .refine((policy) => !policy.allowAdvance || policy.maxAdvanceDays >= 1, {
      error: t('timeOff:errors.maxAdvanceRequiredWhenAllowed'),
      path: ['maxAdvanceDays'],
    });

export type PolicyFormInput = z.input<ReturnType<typeof policySchema>>;
export type PolicyFormValues = z.output<ReturnType<typeof policySchema>>;

/** Shared fields between create and update — `code` is create-only (immutable after creation). */
const timeOffTypeBaseSchema = (t: TFunction) =>
  z
    .object({
      name: z.string().min(1, { error: t('timeOff:errors.typeNameRequired') }),
      consumesVacationBalance: z.boolean(),
      requiresApproval: z.boolean(),
      allowsRetroactive: z.boolean(),
      allowsHourly: z.boolean(),
      requiresAttachment: z.boolean(),
      order: z.coerce.number().int().min(0),
    })
    .refine((type) => !(type.consumesVacationBalance && type.allowsHourly), {
      error: t('timeOff:errors.typeCannotBeHourlyAndConsumeBalance'),
      path: ['allowsHourly'],
    });

export const createTimeOffTypeSchema = (t: TFunction) =>
  timeOffTypeBaseSchema(t).and(
    z.object({ code: z.string().min(1, { error: t('timeOff:errors.typeCodeRequired') }) }),
  );

export const updateTimeOffTypeSchema = (t: TFunction) => timeOffTypeBaseSchema(t);

export type CreateTimeOffTypeFormInput = z.input<ReturnType<typeof createTimeOffTypeSchema>>;
export type CreateTimeOffTypeFormValues = z.output<ReturnType<typeof createTimeOffTypeSchema>>;
export type UpdateTimeOffTypeFormValues = z.output<ReturnType<typeof updateTimeOffTypeSchema>>;

/** One tier of the seniority matrix. `toYear: ''` (empty string from the form) means open-ended. */
export const seniorityMatrixEntrySchema = (t: TFunction) =>
  z.object({
    fromYear: z.coerce
      .number({ error: t('timeOff:errors.matrixYearRequired') })
      .int()
      .min(0),
    toYear: z.preprocess(
      (value) => (value === '' || value === undefined ? null : value),
      z.coerce.number().int().nullable(),
    ),
    days: z.coerce
      .number({ error: t('timeOff:errors.matrixDaysRequired') })
      .int()
      .min(0),
  });

export type SeniorityMatrixEntryFormInput = z.input<ReturnType<typeof seniorityMatrixEntrySchema>>;
export type SeniorityMatrixEntryFormValues = z.output<
  ReturnType<typeof seniorityMatrixEntrySchema>
>;

/**
 * Full-replace matrix schema. Enforces the same invariant the backend does
 * (`TimeOff.MatrixHasGapOrOverlap` / `TimeOff.MatrixMissingOpenEndedEntry`):
 * entries must be contiguous, ordered, and end with exactly one open-ended tier.
 */
export const seniorityMatrixSchema = (t: TFunction) =>
  z
    .object({
      entries: z
        .array(seniorityMatrixEntrySchema(t))
        .min(1, { error: t('timeOff:errors.matrixEmpty') }),
    })
    .refine(({ entries }) => isContiguousOpenEnded(entries), {
      error: t('timeOff:errors.matrixGapOrOverlap'),
      path: ['entries'],
    });

function isContiguousOpenEnded(entries: { fromYear: number; toYear: number | null }[]): boolean {
  if (entries.length === 0) return false;
  if (entries[entries.length - 1].toYear !== null) return false;
  for (let i = 0; i < entries.length - 1; i++) {
    if (entries[i].toYear === null) return false;
    if (entries[i].toYear !== entries[i + 1].fromYear) return false;
  }
  return true;
}

export type SeniorityMatrixFormInput = z.input<ReturnType<typeof seniorityMatrixSchema>>;
export type SeniorityMatrixFormValues = z.output<ReturnType<typeof seniorityMatrixSchema>>;

export const holidaySchema = (t: TFunction) =>
  z.object({
    date: z.string().min(1, { error: t('timeOff:errors.holidayDateRequired') }),
    name: z.string().min(1, { error: t('timeOff:errors.holidayNameRequired') }),
    isOfficial: z.boolean(),
  });

export type HolidayFormValues = z.output<ReturnType<typeof holidaySchema>>;

/** One day within a request form. `isFullDay: false` requires both times. */
export const requestDaySchema = (t: TFunction) =>
  z
    .object({
      date: z.string().min(1, { error: t('timeOff:errors.requestDayRequired') }),
      isFullDay: z.boolean(),
      startTime: z.string().optional().default(''),
      endTime: z.string().optional().default(''),
    })
    .refine((day) => day.isFullDay || (day.startTime !== '' && day.endTime !== ''), {
      error: t('timeOff:errors.requestDayTimeRequired'),
      path: ['startTime'],
    });

/**
 * Request creation form schema. Mirrors `TimeOff.DuplicateRequestDay` and
 * `TimeOff.InvalidDayTimeRange` client-side so the user sees the problem
 * before submitting.
 */
export const createRequestSchema = (t: TFunction) =>
  z
    .object({
      typeId: z.string().min(1, { error: t('timeOff:errors.requestTypeRequired') }),
      days: z.array(requestDaySchema(t)).min(1, { error: t('timeOff:errors.requestDaysEmpty') }),
      reason: z.string().optional(),
    })
    .refine(({ days }) => new Set(days.map((day) => day.date)).size === days.length, {
      error: t('timeOff:errors.requestDuplicateDay'),
      path: ['days'],
    });

export type CreateRequestFormInput = z.input<ReturnType<typeof createRequestSchema>>;
export type CreateRequestFormValues = z.output<ReturnType<typeof createRequestSchema>>;

/** `TimeOff` adjustment direction — purely a UI convenience: the API only takes a signed `days` number. */
export const ADJUSTMENT_TYPE_OPTIONS = ['add', 'subtract'] as const;

/**
 * `amount` is the positive magnitude the pill-selected `adjustmentType` sign
 * applies to — the dialog computes the signed `days` value expected by
 * `CreateMemberAdjustmentPayload` at submit time, not here.
 */
export const memberAdjustmentSchema = (t: TFunction) =>
  z.object({
    adjustmentType: z.enum(ADJUSTMENT_TYPE_OPTIONS, {
      error: t('timeOff:errors.adjustmentDaysRequired'),
    }),
    amount: z.coerce
      .number({ error: t('timeOff:errors.adjustmentDaysRequired') })
      .int()
      .min(1, { error: t('timeOff:errors.adjustmentDaysNonZero') }),
    reason: z.string().min(1, { error: t('timeOff:errors.adjustmentReasonRequired') }),
  });

export type MemberAdjustmentFormInput = z.input<ReturnType<typeof memberAdjustmentSchema>>;
export type MemberAdjustmentFormValues = z.output<ReturnType<typeof memberAdjustmentSchema>>;

/**
 * Historical request form schema — Owner/Admin only. Mirrors
 * `createRequestSchema`'s day handling (`requestDaySchema`) but identifies
 * the member by email and the type by code, per the confirmed `api.json`
 * shape for `CreateHistoricalTimeOffRequestRequest` (not by `id` like the
 * live create-request endpoint).
 */
export const historicalRequestSchema = (t: TFunction) =>
  z
    .object({
      userEmail: z
        .string()
        .min(1, { error: t('timeOff:errors.historicalEmailRequired') })
        .email({ error: t('timeOff:errors.historicalEmailInvalid') }),
      typeCode: z.string().min(1, { error: t('timeOff:errors.requestTypeRequired') }),
      cycleNumber: z.preprocess(
        (value) => (value === '' || value === undefined ? undefined : value),
        z.coerce.number().int().min(0).optional(),
      ),
      days: z.array(requestDaySchema(t)).min(1, { error: t('timeOff:errors.requestDaysEmpty') }),
      reason: z.string().optional(),
    })
    .refine(({ days }) => new Set(days.map((day) => day.date)).size === days.length, {
      error: t('timeOff:errors.requestDuplicateDay'),
      path: ['days'],
    });

export type HistoricalRequestFormInput = z.input<ReturnType<typeof historicalRequestSchema>>;
export type HistoricalRequestFormValues = z.output<ReturnType<typeof historicalRequestSchema>>;
